import Alert from '@codegouvfr/react-dsfr/Alert';
import Button from '@codegouvfr/react-dsfr/Button';
import { cx } from '@codegouvfr/react-dsfr/fr/cx';
import clsx from 'clsx';
import {
  groupBy,
  isEmpty,
  isEqual,
  isNil,
  mapValues,
  omitBy,
  uniq
} from 'lodash-es';
import type { Department } from 'maestro-shared/referential/Department';
import type { Region } from 'maestro-shared/referential/Region';
import type { Company } from 'maestro-shared/schema/Company/Company';
import {
  filteredLocalPrescriptions,
  type LocalPrescription
} from 'maestro-shared/schema/LocalPrescription/LocalPrescription';
import { hasUnviewedChange } from 'maestro-shared/schema/LocalPrescription/LocalPrescriptionChange';
import {
  type LocalPrescriptionKey,
  type LocalPrescriptionKeyString,
  toLocalPrescriptionKeyString,
  toLocalPrescriptionRegionalKeyString
} from 'maestro-shared/schema/LocalPrescription/LocalPrescriptionKey';
import type { SubstanceKindLaboratory } from 'maestro-shared/schema/LocalPrescription/LocalPrescriptionSubstanceKindLaboratory';
import { FindPrescriptionOptions } from 'maestro-shared/schema/Prescription/FindPrescriptionOptions';
import {
  type Prescription,
  sortPrescriptions
} from 'maestro-shared/schema/Prescription/Prescription';
import type { PrescriptionImportResult } from 'maestro-shared/schema/Prescription/PrescriptionImport';
import { hasDepartmentalEchelon } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlanEchelon';
import type { ProgrammingPlanChecked } from 'maestro-shared/schema/ProgrammingPlan/ProgrammingPlans';
import type { SubstanceKind } from 'maestro-shared/schema/Substance/SubstanceKind';
import {
  changeViewAudienceForRole,
  isDepartmentalRole,
  isNationalRole,
  isRegionalRole
} from 'maestro-shared/schema/User/UserRole';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import AppToast from 'src/components/_app/AppToast/AppToast';
import PrescriptionActionBar from 'src/components/Prescription/PrescriptionActionBar/PrescriptionActionBar';
import SelectionActionBar from 'src/components/SelectionActionBar/SelectionActionBar';
import { useAuthentication } from 'src/hooks/useAuthentication';
import { usePrescriptionFilters } from 'src/hooks/usePrescriptionFilters';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import prescriptionsSlice, {
  PrescriptionFilters
} from 'src/store/reducers/prescriptionsSlice';
import PrescriptionImportModal, {
  prescriptionImportModal
} from 'src/views/ProgrammingView/ProgrammingPrescriptionList/PrescriptionImportModal';
import ProgrammingPrescriptionListHeader from 'src/views/ProgrammingView/ProgrammingPrescriptionList/ProgrammingPrescriptionListHeader';
import { assert, type Equals } from 'tsafe';
import { ApiClientContext } from '../../../services/apiClient';
import { getApiUrl } from '../../../utils/fetchUtils';
import { groupSubstanceKindsLaboratoriesBySample } from '../../../utils/sampleLaboratories';
import ProgrammingPrescriptionFilters from '../ProgrammingPrescriptionFilters/ProgrammingPrescriptionFilters';
import ProgrammingPrescriptionTable from '../ProgrammingPrescriptionTable/ProgrammingPrescriptionTable';
import { findPrescriptionPlan } from '../prescriptionPlan';
import BulkAssignLaboratoriesModal, {
  bulkAssignLaboratoriesModal
} from './BulkAssignLaboratoriesModal';

interface Props {
  programmingPlans: ProgrammingPlanChecked[];
  yearProgrammingPlans: ProgrammingPlanChecked[];
  region?: Region;
  department?: Department;
  companies?: Company[];
  onPendingChange?: (hasPendingChanges: boolean, reset: () => void) => void;
  onChangeDismissalCandidatesChange?: (prescriptionIds: string[]) => void;
}

const ProgrammingPrescriptionList = ({
  programmingPlans,
  yearProgrammingPlans,
  region,
  department,
  companies,
  onPendingChange,
  onChangeDismissalCandidatesChange,
  ..._rest
}: Props) => {
  assert<Equals<keyof typeof _rest, never>>();

  const apiClient = useContext(ApiClientContext);
  const dispatch = useAppDispatch();

  const { prescriptionFilters } = useAppSelector(
    (state) => state.prescriptions
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const {
    hasNationalView,
    hasRegionalView,
    hasDepartmentalView,
    hasUserPermission,
    hasUserPrescriptionPermission,
    hasUserLocalPrescriptionPermission,
    userRole,
    user
  } = useAuthentication();

  const canBulkAssignLaboratories =
    (hasRegionalView || hasDepartmentalView) && !hasNationalView;

  const [selectedPrescriptions, setSelectedPrescriptions] = useState<
    Prescription[]
  >([]);

  const [pendingLocalChanges, setPendingLocalChanges] = useState<
    Map<
      LocalPrescriptionKeyString,
      { key: LocalPrescriptionKey; sampleCount: number }
    >
  >(new Map());
  const [pendingLaboratoryChanges, setPendingLaboratoryChanges] = useState<
    Map<
      LocalPrescriptionKeyString,
      {
        key: LocalPrescriptionKey;
        substanceKindsLaboratories: SubstanceKindLaboratory[];
      }
    >
  >(new Map());
  const [pendingPrescriptionSampleCounts, setPendingPrescriptionSampleCounts] =
    useState<Map<string, number>>(new Map());
  const [importedPlanIdByPrescriptionId, setImportedPlanIdByPrescriptionId] =
    useState<Map<string, string>>(new Map());
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [importResult, setImportResult] = useState<
    PrescriptionImportResult | undefined
  >(undefined);

  const canImport =
    (hasNationalView && hasUserPermission('updatePrescription')) ||
    hasUserPermission('distributePrescriptionToDepartments') ||
    hasUserPermission('distributePrescriptionToSlaughterhouses');

  const hasPendingChanges =
    pendingLocalChanges.size > 0 ||
    pendingLaboratoryChanges.size > 0 ||
    pendingPrescriptionSampleCounts.size > 0;

  const [savePrescriptions] = apiClient.useSavePrescriptionsMutation();

  const { reduceFilters } = usePrescriptionFilters(yearProgrammingPlans);

  const changeFilter = useCallback(
    (findFilter: Partial<typeof prescriptionFilters>) => {
      const filteredParams = reduceFilters(prescriptionFilters, findFilter);
      const urlSearchParams = new URLSearchParams(searchParams);
      for (const key of PrescriptionFilters.keyof().options) {
        urlSearchParams.delete(key);
      }
      const nextParams = omitBy(
        mapValues(filteredParams, (value) => value?.toString()),
        isEmpty
      ) as Record<string, string>;
      for (const [key, value] of Object.entries(nextParams)) {
        urlSearchParams.set(key, value);
      }
      setSearchParams(urlSearchParams, { replace: true });
    },
    [reduceFilters, prescriptionFilters, searchParams, setSearchParams]
  );

  const planIds = useMemo(
    () => programmingPlans.map((p) => p.id),
    [programmingPlans]
  );

  const getPrescriptionPlan = useCallback(
    (prescription: Prescription): ProgrammingPlanChecked | undefined =>
      findPrescriptionPlan(yearProgrammingPlans, prescription),
    [yearProgrammingPlans]
  );

  const findPrescriptionOptions = useMemo(
    () => ({
      programmingPlanIds: planIds,
      year: prescriptionFilters.year,
      programmingSubPlanIds: prescriptionFilters.programmingSubPlanIds,
      programmingPlanDomainIds: prescriptionFilters.programmingPlanDomainIds,
      contexts: prescriptionFilters.contexts,
      matrixKinds: prescriptionFilters.matrixKinds,
      coordinatorIds: prescriptionFilters.coordinatorIds,
      laboratoryIds: prescriptionFilters.laboratoryIds,
      missingDistribution: prescriptionFilters.missingDistribution,
      withNovelty: prescriptionFilters.withNovelty,
      missingLaboratory: prescriptionFilters.missingLaboratory,
      withSampleCountOnly: !hasNationalView,
      subPlanStage: prescriptionFilters.stage,
      region,
      department
    }),
    [planIds, prescriptionFilters, region, department, hasNationalView]
  );

  const findFilterOptionsCountsOptions = useMemo(
    () => ({
      programmingPlanIds: yearProgrammingPlans.map((plan) => plan.id),
      year: prescriptionFilters.year,
      withSampleCountOnly: !hasNationalView,
      subPlanStage: prescriptionFilters.stage,
      region,
      department
    }),
    [
      yearProgrammingPlans,
      prescriptionFilters.year,
      prescriptionFilters.stage,
      region,
      department,
      hasNationalView
    ]
  );

  const {
    data: allPrescriptions,
    refetch: refetchPrescriptions,
    isUninitialized: isPrescriptionsUninitialized
  } = apiClient.useFindPrescriptionsQuery(findPrescriptionOptions, {
    skip:
      !planIds.length ||
      !FindPrescriptionOptions.safeParse(findPrescriptionOptions).success
  });

  const getPlanForPrescriptionId = useCallback(
    (prescriptionId: string): ProgrammingPlanChecked | undefined => {
      const prescription = allPrescriptions?.find(
        (r) => r.id === prescriptionId
      );
      return (
        prescription && findPrescriptionPlan(yearProgrammingPlans, prescription)
      );
    },
    [yearProgrammingPlans, allPrescriptions]
  );

  const { data: departmentCompanies } = apiClient.useFindCompaniesQuery(
    {
      region,
      department,
      kinds: ['POULTRY_SLAUGHTERHOUSE', 'MEAT_SLAUGHTERHOUSE']
    },
    { skip: !region || !department }
  );

  const effectiveCompanies = department
    ? (departmentCompanies ?? [])
    : companies;

  const findLocalPrescriptionOptions = useMemo(
    () => ({
      programmingPlanIds: planIds,
      programmingSubPlanIds: prescriptionFilters.programmingSubPlanIds,
      contexts: prescriptionFilters.contexts,
      region,
      department,
      includes: [
        'sampleCounts' as const,
        ...(hasUserPermission('commentPrescription')
          ? ['comments' as const]
          : []),
        ...(hasUserPermission('updatePrescriptionLaboratories')
          ? ['laboratories' as const]
          : []),
        'pendingChanges' as const
      ]
    }),
    [planIds, prescriptionFilters, region, department, hasUserPermission]
  );

  const {
    data: allLocalPrescriptions,
    refetch: refetchLocalPrescriptions,
    isUninitialized: isLocalPrescriptionsUninitialized
  } = apiClient.useFindLocalPrescriptionsQuery(findLocalPrescriptionOptions, {
    skip: !findLocalPrescriptionOptions.programmingPlanIds?.length
  });

  const allPrescriptionsWithPending = useMemo(
    () =>
      allPrescriptions?.map((p) => {
        const pending = pendingPrescriptionSampleCounts.get(p.id);
        return pending !== undefined ? { ...p, sampleCount: pending } : p;
      }),
    [allPrescriptions, pendingPrescriptionSampleCounts]
  );

  const allLocalPrescriptionsWithPending = useMemo(() => {
    const existing = (allLocalPrescriptions ?? []).map((lp) => {
      const key = toLocalPrescriptionKeyString({
        prescriptionId: lp.prescriptionId,
        region: lp.region,
        department: lp.department ?? undefined,
        companySiret: lp.companySiret ?? undefined
      });
      const pending = pendingLocalChanges.get(key);
      const pendingLab = pendingLaboratoryChanges.get(key);
      return {
        ...lp,
        ...(pending ? { sampleCount: pending.sampleCount } : {}),
        ...(pendingLab
          ? {
              substanceKindsLaboratories: pendingLab.substanceKindsLaboratories
            }
          : {})
      };
    });

    const existingKeys = new Set(
      existing.map((lp) =>
        toLocalPrescriptionKeyString({
          prescriptionId: lp.prescriptionId,
          region: lp.region,
          department: lp.department ?? undefined,
          companySiret: lp.companySiret ?? undefined
        })
      )
    );
    const pendingOnly: LocalPrescription[] = Array.from(
      pendingLocalChanges.values()
    )
      .filter(({ key }) => !existingKeys.has(toLocalPrescriptionKeyString(key)))
      .map(({ key, sampleCount }) => ({
        prescriptionId: key.prescriptionId,
        region: key.region,
        department: key.department,
        companySiret: key.companySiret,
        sampleCount
      }));

    return [...existing, ...pendingOnly];
  }, [allLocalPrescriptions, pendingLocalChanges, pendingLaboratoryChanges]);

  const handleImported = useCallback(
    (result: PrescriptionImportResult) => {
      setImportResult(result);

      setImportedPlanIdByPrescriptionId((current) => {
        const next = new Map(current);
        for (const { prescriptionId, programmingPlanId } of [
          ...result.localChanges,
          ...result.totals
        ]) {
          next.set(prescriptionId, programmingPlanId);
        }
        return next;
      });

      setPendingLocalChanges((current) => {
        const next = new Map(current);
        for (const {
          prescriptionId,
          region,
          department,
          companySiret,
          sampleCount
        } of result.localChanges) {
          const key = {
            prescriptionId,
            region,
            department: department ?? undefined,
            companySiret: companySiret ?? undefined
          };
          const keyString = toLocalPrescriptionKeyString(key);
          const persistedSampleCount = (allLocalPrescriptions ?? []).find(
            (localPrescription) =>
              toLocalPrescriptionKeyString(localPrescription) === keyString
          )?.sampleCount;

          if (persistedSampleCount === sampleCount) {
            next.delete(keyString);
          } else {
            next.set(keyString, { key, sampleCount });
          }
        }
        return next;
      });

      setPendingPrescriptionSampleCounts((current) => {
        const next = new Map(current);
        for (const { prescriptionId, sampleCount } of result.totals) {
          const persistedSampleCount = allPrescriptions?.find(
            (prescription) => prescription.id === prescriptionId
          )?.sampleCount;

          if (persistedSampleCount === sampleCount) {
            next.delete(prescriptionId);
          } else {
            next.set(prescriptionId, sampleCount);
          }
        }
        return next;
      });
    },
    [allLocalPrescriptions, allPrescriptions]
  );

  const {
    data: lastPrescriptionCounts,
    currentData: currentPrescriptionCounts,
    isFetching: isFetchingPrescriptionCounts,
    refetch: refetchPrescriptionCounts,
    isUninitialized: isPrescriptionCountsUninitialized
  } = apiClient.useFindPrescriptionCountsQuery(findPrescriptionOptions, {
    skip: !planIds.length
  });

  const prescriptionCounts =
    currentPrescriptionCounts ??
    (isFetchingPrescriptionCounts ? lastPrescriptionCounts : undefined);

  const { data: filterOptionsCounts } =
    apiClient.useFindPrescriptionCountsQuery(findFilterOptionsCountsOptions, {
      skip: !findFilterOptionsCountsOptions.programmingPlanIds.length
    });

  const prescriptions = useMemo(
    () =>
      allPrescriptionsWithPending &&
      sortPrescriptions(allPrescriptionsWithPending, programmingPlans),
    [allPrescriptionsWithPending, programmingPlans]
  );

  const stageCounts = useMemo(
    () => filterOptionsCounts?.stageCounts ?? [],
    [filterOptionsCounts]
  );

  const filterOptions = useMemo(
    () => ({
      plans: yearProgrammingPlans.filter((plan) =>
        (filterOptionsCounts?.programmingPlanIds ?? []).includes(plan.id)
      ),
      programmingSubPlanIds: filterOptionsCounts?.programmingSubPlanIds ?? [],
      matrixKinds: filterOptionsCounts?.matrixKinds ?? [],
      contexts: filterOptionsCounts?.contexts ?? []
    }),
    [filterOptionsCounts, yearProgrammingPlans]
  );

  const localPrescriptions = useMemo(
    () =>
      filteredLocalPrescriptions(allLocalPrescriptionsWithPending ?? [], {
        region,
        department,
        companies,
        regionalPrescriptionIds: (allPrescriptions ?? [])
          .filter((prescription) => {
            const prescriptionPlan = findPrescriptionPlan(
              programmingPlans,
              prescription
            );
            return (
              !isNil(prescriptionPlan) &&
              !hasDepartmentalEchelon(prescriptionPlan.distributionKind)
            );
          })
          .map((prescription) => prescription.id)
      }),
    [
      allLocalPrescriptionsWithPending,
      allPrescriptions,
      programmingPlans,
      department,
      region,
      companies
    ]
  );

  const subLocalPrescriptions = useMemo(
    () =>
      allLocalPrescriptionsWithPending
        ?.filter((_) => prescriptions?.some((p) => p.id === _.prescriptionId))
        .filter((_) => {
          if (department) {
            return (
              _.region === region &&
              _.department === department &&
              !isNil(_.companySiret)
            );
          }
          return _.region === region && !isNil(_.department);
        }),
    [prescriptions, allLocalPrescriptionsWithPending, department, region]
  );

  const clearsNoveltyOnNavigation =
    !isNil(userRole) &&
    ['Admin', 'Applied'].includes(changeViewAudienceForRole(userRole));

  useEffect(() => {
    if (!clearsNoveltyOnNavigation) {
      onChangeDismissalCandidatesChange?.([]);
      return;
    }
    const candidates = (prescriptions ?? [])
      .filter((prescription) =>
        localPrescriptions.some(
          (lp) =>
            lp.prescriptionId === prescription.id &&
            hasUnviewedChange(lp.changedAt)
        )
      )
      .map((prescription) => prescription.id);
    onChangeDismissalCandidatesChange?.(candidates);
  }, [
    clearsNoveltyOnNavigation,
    prescriptions,
    localPrescriptions,
    onChangeDismissalCandidatesChange
  ]);

  useEffect(() => {
    if (
      searchParams.get('prescriptionId') &&
      searchParams.get('commentsRegion')
    ) {
      const prescription = (prescriptions ?? []).find(
        (prescription) => prescription.id === searchParams.get('prescriptionId')
      );
      const regionalPrescription = localPrescriptions?.find(
        (regionalPrescription) =>
          regionalPrescription.prescriptionId ===
            searchParams.get('prescriptionId') &&
          regionalPrescription.region === searchParams.get('commentsRegion')
      );
      const commentsPlan = prescription
        ? getPrescriptionPlan(prescription)
        : undefined;
      if (prescription && regionalPrescription && commentsPlan) {
        dispatch(
          prescriptionsSlice.actions.setPrescriptionCommentsData({
            viewBy: 'Prescription',
            programmingPlan: commentsPlan,
            prescription,
            regionalCommentsList: [regionalPrescription].map((rcp) => ({
              region: rcp.region,
              department: rcp.department,
              comments: rcp.comments ?? []
            }))
          })
        );
      }
      setTimeout(() => {
        searchParams.delete('prescriptionId');
        searchParams.delete('commentsRegion');
        setSearchParams(searchParams, { replace: true });
      }, 1000);
    }
  }, [searchParams, localPrescriptions]);

  const openComments = useCallback(
    (prescription: Prescription) => {
      const plan = getPrescriptionPlan(prescription);
      if (!plan) {
        return;
      }
      const regionalCommentsList = (allLocalPrescriptions ?? [])
        .filter(
          (_) => _.prescriptionId === prescription.id && isNil(_.department)
        )
        .map((_) => ({
          region: _.region,
          department: _.department,
          comments: _.comments ?? []
        }));

      const commentedRegions = regionalCommentsList.filter(
        (_) => _.comments.length > 0
      );

      dispatch(
        prescriptionsSlice.actions.setPrescriptionCommentsData({
          viewBy: 'Prescription',
          programmingPlan: plan,
          prescription,
          regionalCommentsList: commentedRegions.length
            ? commentedRegions
            : regionalCommentsList
        })
      );
    },
    [dispatch, getPrescriptionPlan, allLocalPrescriptions]
  );

  const canUpdatePrescription = useCallback(
    (programmingPlanId: string) => {
      const plan = yearProgrammingPlans.find(
        (programmingPlan) => programmingPlan.id === programmingPlanId
      );
      return !!plan && hasUserPrescriptionPermission(plan)?.update === true;
    },
    [yearProgrammingPlans, hasUserPrescriptionPermission]
  );

  const changeLocalPrescriptionCount = useCallback(
    (key: LocalPrescriptionKey, count: number) => {
      setPendingLocalChanges((prev) => {
        const next = new Map(prev);
        next.set(toLocalPrescriptionKeyString(key), {
          key,
          sampleCount: count
        });
        return next;
      });
    },
    []
  );

  const changeLocalPrescriptionLaboratories = useCallback(
    (
      key: LocalPrescriptionKey,
      substanceKindsLaboratories: SubstanceKindLaboratory[]
    ) => {
      setPendingLaboratoryChanges((prev) => {
        const next = new Map(prev);
        next.set(toLocalPrescriptionKeyString(key), {
          key,
          substanceKindsLaboratories
        });
        return next;
      });
    },
    []
  );

  const handleReset = useCallback(() => {
    setPendingLocalChanges(new Map());
    setPendingLaboratoryChanges(new Map());
    setPendingPrescriptionSampleCounts(new Map());
    setImportedPlanIdByPrescriptionId(new Map());
  }, []);

  useEffect(() => {
    onPendingChange?.(hasPendingChanges, handleReset);
  }, [hasPendingChanges]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setSaveError(false);
    try {
      const planIdForPrescriptionId = (
        prescriptionId: string
      ): string | undefined =>
        importedPlanIdByPrescriptionId.get(prescriptionId) ??
        getPlanForPrescriptionId(prescriptionId)?.id;

      const plainLocalChanges = Array.from(pendingLocalChanges.values()).filter(
        ({ key }) => !key.companySiret
      );
      const slaughterhouseChangeGroups = Object.values(
        groupBy(
          Array.from(pendingLocalChanges.values()).filter(
            ({ key }) => key.companySiret
          ),
          ({ key }) => toLocalPrescriptionRegionalKeyString(key)
        )
      );

      const localPrescriptionUpdates = [
        ...plainLocalChanges.map(({ key, sampleCount }) => ({
          prescriptionId: key.prescriptionId,
          region: key.region,
          department: key.department,
          update: {
            key: 'sampleCount' as const,
            sampleCount
          }
        })),
        ...slaughterhouseChangeGroups.map((entries) => {
          const { prescriptionId, region, department } = entries[0].key;
          const pendingBySiret = new Map(
            entries.map(({ key, sampleCount }) => [
              key.companySiret as string,
              sampleCount
            ])
          );
          const slaughterhouseSampleCounts = (effectiveCompanies ?? []).map(
            (company) => ({
              companySiret: company.siret,
              sampleCount:
                pendingBySiret.get(company.siret) ??
                subLocalPrescriptions?.find(
                  (sp) =>
                    sp.prescriptionId === prescriptionId &&
                    sp.companySiret === company.siret
                )?.sampleCount ??
                0
            })
          );
          return {
            prescriptionId,
            region,
            department,
            update: {
              key: 'slaughterhouseSampleCounts' as const,
              slaughterhouseSampleCounts
            }
          };
        }),
        ...Array.from(pendingLaboratoryChanges.values()).map(
          ({ key, substanceKindsLaboratories }) => ({
            prescriptionId: key.prescriptionId,
            region: key.region,
            department: key.department,
            update: {
              key: 'laboratories' as const,
              substanceKindsLaboratories
            }
          })
        )
      ];

      const prescriptionUpdates = Array.from(
        pendingPrescriptionSampleCounts.entries()
      )
        .map(([prescriptionId, sampleCount]) => ({
          prescriptionId,
          sampleCount
        }))
        .filter(({ prescriptionId }) => {
          const planId = planIdForPrescriptionId(prescriptionId);
          return isNil(planId) || canUpdatePrescription(planId);
        });

      await savePrescriptions({
        prescriptionUpdates,
        localPrescriptionUpdates
      }).unwrap();
      await Promise.all([
        isPrescriptionsUninitialized ? undefined : refetchPrescriptions(),
        isLocalPrescriptionsUninitialized
          ? undefined
          : refetchLocalPrescriptions(),
        isPrescriptionCountsUninitialized
          ? undefined
          : refetchPrescriptionCounts()
      ]);
      setPendingLocalChanges(new Map());
      setPendingLaboratoryChanges(new Map());
      setPendingPrescriptionSampleCounts(new Map());
      setImportedPlanIdByPrescriptionId(new Map());
      setSaveSuccess(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setSaveError(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSaving(false);
    }
  }, [
    pendingLocalChanges,
    pendingLaboratoryChanges,
    pendingPrescriptionSampleCounts,
    savePrescriptions,
    canUpdatePrescription,
    getPlanForPrescriptionId,
    importedPlanIdByPrescriptionId,
    effectiveCompanies,
    subLocalPrescriptions,
    refetchPrescriptions,
    refetchLocalPrescriptions,
    refetchPrescriptionCounts,
    isPrescriptionsUninitialized,
    isLocalPrescriptionsUninitialized,
    isPrescriptionCountsUninitialized
  ]);

  const saveSuccessMessage = useMemo(() => {
    if (userRole === 'AdministratorMaestro' || userRole === 'AdministratorBGIR')
      return 'Vos modifications ont été enregistrées avec succès. Pensez à les diffuser aux régions dans "Suivi des plans".';
    if (isNationalRole(userRole))
      return 'Vos modifications ont été enregistrées avec succès. Pensez à les diffuser à l\'administrateur et/ou aux régions dans "Suivi des plans".';
    if (isRegionalRole(userRole)) {
      const hasRegionalKind = programmingPlans.some(
        (plan) => !hasDepartmentalEchelon(plan.distributionKind)
      );
      const hasDepartmentalEchelonKind = programmingPlans.some((plan) =>
        hasDepartmentalEchelon(plan.distributionKind)
      );
      if (hasRegionalKind && !hasDepartmentalEchelonKind) {
        return 'Vos modifications ont été enregistrées avec succès. Pensez à les diffuser aux préleveurs dans "Suivi des plans".';
      }
      if (hasDepartmentalEchelonKind && !hasRegionalKind) {
        return 'Vos modifications ont été enregistrées avec succès. Pensez à les diffuser aux départements dans "Suivi des plans".';
      }
      return 'Vos modifications ont été enregistrées avec succès. Pensez à les diffuser aux préleveurs et/ou aux départements dans "Suivi des plans".';
    }
    if (isDepartmentalRole(userRole))
      return 'Vos modifications ont été enregistrées avec succès. Pensez à les diffuser aux préleveurs dans "Suivi des plans".';
    return 'Vos modifications ont été enregistrées avec succès.';
  }, [userRole, programmingPlans]);

  const hasGroupedUpdatePermission = useMemo(
    () =>
      localPrescriptions?.some((regionalPrescription) => {
        const prescription = allPrescriptions?.find(
          (p) => p.id === regionalPrescription.prescriptionId
        );
        const plan = prescription
          ? getPrescriptionPlan(prescription)
          : programmingPlans[0];
        return hasUserLocalPrescriptionPermission(plan, regionalPrescription)
          ?.updateLaboratories;
      }),
    [
      programmingPlans,
      localPrescriptions,
      allPrescriptions,
      getPrescriptionPlan,
      hasUserLocalPrescriptionPermission
    ]
  );

  const togglePrescriptionSelection = hasGroupedUpdatePermission
    ? (prescription: Prescription) => {
        setSelectedPrescriptions((prevState) =>
          prevState.some((_) => _.id === prescription.id)
            ? prevState.filter((_) => _.id !== prescription.id)
            : [...prevState, prescription]
        );
      }
    : undefined;

  const [bulkAssignBannerHeight, setBulkAssignBannerHeight] = useState(0);

  const pendingLocalKeys = useMemo(
    () => new Set(pendingLocalChanges.keys()),
    [pendingLocalChanges]
  );

  const pendingLaboratoryKeys = useMemo(
    () => new Set(pendingLaboratoryChanges.keys()),
    [pendingLaboratoryChanges]
  );

  const pendingPrescriptionIds = useMemo(
    () => new Set(pendingPrescriptionSampleCounts.keys()),
    [pendingPrescriptionSampleCounts]
  );

  const changePrescriptionSampleCount = useCallback(
    (prescription: Prescription, sampleCount: number) => {
      setPendingPrescriptionSampleCounts((prev) => {
        const next = new Map(prev);
        next.set(prescription.id, sampleCount);
        return next;
      });
    },
    []
  );

  const laboratorySlotsFor = useCallback(
    (prescription: Prescription) => {
      const plan = getPrescriptionPlan(prescription);
      if (!plan) {
        return [];
      }
      const isLaboratoryEchelon = hasDepartmentalEchelon(plan.distributionKind)
        ? !!department
        : !department;
      if (!isLaboratoryEchelon) {
        return [];
      }
      const regional = localPrescriptions.find(
        (lp) => lp.prescriptionId === prescription.id
      );
      if (!regional) {
        return [];
      }
      const subPlan = plan.subPlans.find(
        (sp) => sp.id === prescription.programmingSubPlanId
      );
      return [...(subPlan?.substanceKinds ?? [])]
        .sort()
        .map((substanceKind) => ({
          substanceKind,
          laboratoryId: regional.substanceKindsLaboratories?.find(
            (skl) => skl.substanceKind === substanceKind
          )?.laboratoryId
        }));
    },
    [getPrescriptionPlan, localPrescriptions, department]
  );

  const sampleSubstanceKindsFor = useCallback(
    (prescription: Prescription) =>
      groupSubstanceKindsLaboratoriesBySample(
        getPrescriptionPlan(prescription)?.subPlans.find(
          (sp) => sp.id === prescription.programmingSubPlanId
        )?.samples ?? null,
        laboratorySlotsFor(prescription)
      ).map(({ substanceKinds }) => ({ substanceKinds })),
    [getPrescriptionPlan, laboratorySlotsFor]
  );

  const bulkAssignCheck = useMemo(():
    | {
        commonSlots: { substanceKind: SubstanceKind; laboratoryId?: string }[];
        commonSamples: { substanceKinds: SubstanceKind[] }[];
        blockReason?: undefined;
      }
    | { commonSlots: []; commonSamples: []; blockReason: string } => {
    const slotsPerPrescription = selectedPrescriptions.map(laboratorySlotsFor);
    const [firstSlots, ...otherSlots] = slotsPerPrescription;

    const sameSubstanceKinds =
      firstSlots &&
      firstSlots.length > 0 &&
      otherSlots.every(
        (slots) =>
          slots.length === firstSlots.length &&
          slots.every((s, i) => s.substanceKind === firstSlots[i].substanceKind)
      );

    if (!sameSubstanceKinds) {
      return {
        commonSlots: [],
        commonSamples: [],
        blockReason:
          "Les sous-plans sélectionnés n'ont pas le même nombre de laboratoires à attribuer. L'action groupée n'est pas possible."
      };
    }

    const [firstSamples, ...otherSamples] = selectedPrescriptions.map(
      sampleSubstanceKindsFor
    );

    if (!otherSamples.every((samples) => isEqual(samples, firstSamples))) {
      return {
        commonSlots: [],
        commonSamples: [],
        blockReason:
          "Les sous-plans sélectionnés ne regroupent pas les analytes dans les mêmes échantillons. L'action groupée n'est pas possible."
      };
    }

    const slotLaboratoryIds = firstSlots.map((_, index) => {
      const laboratoryIds = new Set(
        slotsPerPrescription
          .map((slots) => slots[index].laboratoryId)
          .filter((id): id is string => !!id)
      );
      return laboratoryIds;
    });

    if (slotLaboratoryIds.some((laboratoryIds) => laboratoryIds.size > 1)) {
      return {
        commonSlots: [],
        commonSamples: [],
        blockReason:
          "Les sous-plans sélectionnés ont des laboratoires déjà attribués différents. L'action groupée n'est pas possible."
      };
    }

    return {
      commonSlots: firstSlots.map((slot, index) => ({
        substanceKind: slot.substanceKind,
        laboratoryId: [...slotLaboratoryIds[index]][0]
      })),
      commonSamples: firstSamples
    };
  }, [selectedPrescriptions, laboratorySlotsFor, sampleSubstanceKindsFor]);

  return (
    <>
      {canImport && prescriptionFilters.year && (
        <PrescriptionImportModal
          year={prescriptionFilters.year}
          onImported={handleImported}
        />
      )}
      <AppToast
        open={importResult?.unrecognized.length === 0}
        description="Fichier repris. Vérifiez les valeurs puis enregistrez."
        onClose={() => setImportResult(undefined)}
      />
      {importResult && importResult.unrecognized.length > 0 && (
        <div className={cx('fr-container', 'fr-px-5w', 'fr-mb-2w')}>
          <Alert
            severity="warning"
            closable
            onClose={() => setImportResult(undefined)}
            title="Fichier repris partiellement."
            description={
              <>
                Les données suivantes n’ont pas été reprises car Maestro ne les
                a pas reconnues :
                <ul>
                  {importResult.unrecognized.map((label) => (
                    <li key={label}>{label}</li>
                  ))}
                </ul>
              </>
            }
          />
        </div>
      )}
      <AppToast
        open={saveSuccess}
        description={saveSuccessMessage}
        onClose={() => setSaveSuccess(false)}
      />
      <AppToast
        open={saveError}
        severity="error"
        description="Une erreur est survenue lors de l'enregistrement. Vos modifications n'ont pas été enregistrées, veuillez réessayer."
        onClose={() => setSaveError(false)}
      />

      {prescriptions && localPrescriptions && prescriptionCounts && (
        <>
          {
            <ProgrammingPrescriptionListHeader
              programmingPlans={programmingPlans}
              counts={prescriptionCounts}
              exportURL={getApiUrl(
                '/prescriptions/export',
                findPrescriptionOptions
              )}
              onImport={
                canImport ? () => prescriptionImportModal.open() : undefined
              }
            />
          }
          <ProgrammingPrescriptionFilters
            options={filterOptions}
            stageCounts={stageCounts}
            filters={prescriptionFilters}
            onChange={changeFilter}
          />
          {prescriptions.length === 0 && (
            <div
              className={clsx(
                cx('fr-container', 'fr-mt-8w', 'fr-px-7w'),
                'align-center'
              )}
            >
              Aucun prélèvement programmé pour les filtres sélectionnés
            </div>
          )}
          {canBulkAssignLaboratories && (
            <SelectionActionBar
              selectedCount={selectedPrescriptions.length}
              itemLabel="sous-plan sélectionné"
              onDeselectAll={() => setSelectedPrescriptions([])}
              onHeightChange={setBulkAssignBannerHeight}
              notice={
                bulkAssignCheck.blockReason
                  ? { description: bulkAssignCheck.blockReason }
                  : undefined
              }
            >
              <Button
                priority="secondary"
                size="small"
                iconId="fr-icon-list-check"
                disabled={!!bulkAssignCheck.blockReason}
                onClick={() => bulkAssignLaboratoriesModal.open()}
                className={cx('fr-ml-3w')}
              >
                Attribuer les laboratoires
              </Button>
            </SelectionActionBar>
          )}
          {prescriptions.length > 0 && (
            <ProgrammingPrescriptionTable
              programmingPlans={programmingPlans}
              prescriptions={prescriptions}
              regionalPrescriptions={localPrescriptions}
              onOpenComments={openComments}
              onChangeLocalPrescriptionCount={changeLocalPrescriptionCount}
              pendingLocalKeys={pendingLocalKeys}
              onChangeLocalPrescriptionLaboratories={
                changeLocalPrescriptionLaboratories
              }
              pendingLaboratoryKeys={pendingLaboratoryKeys}
              {...(hasNationalView
                ? {
                    pendingPrescriptionIds,
                    onChangePrescriptionSampleCount:
                      changePrescriptionSampleCount
                  }
                : userRole === 'Sampler'
                  ? {
                      region: user?.region as Region,
                      department
                    }
                  : {
                      region: user?.region as Region,
                      department,
                      companies: effectiveCompanies,
                      subLocalPrescriptions: subLocalPrescriptions ?? [],
                      selectedPrescriptions,
                      onTogglePrescriptionSelection:
                        togglePrescriptionSelection,
                      topOffset: bulkAssignBannerHeight
                    })}
            />
          )}
        </>
      )}
      {canBulkAssignLaboratories && (
        <BulkAssignLaboratoriesModal
          programmingSubPlanIds={uniq(
            selectedPrescriptions.map((_) => _.programmingSubPlanId)
          )}
          commonSlots={bulkAssignCheck.commonSlots}
          samples={bulkAssignCheck.commonSamples}
          onSubmit={(substanceKindsLaboratories) => {
            for (const prescription of selectedPrescriptions) {
              changeLocalPrescriptionLaboratories(
                {
                  prescriptionId: prescription.id,
                  region: region as Region,
                  department
                },
                substanceKindsLaboratories
              );
            }
            bulkAssignLaboratoriesModal.close();
            setSelectedPrescriptions([]);
          }}
        />
      )}
      {hasPendingChanges && (
        <PrescriptionActionBar
          onReset={handleReset}
          onSave={handleSave}
          isSaving={isSaving}
        />
      )}
    </>
  );
};

export default ProgrammingPrescriptionList;
